import { useI18n } from 'vue-i18n'
import {
  refAutoReset,
  until,
  useBreakpoints,
  useElementSize,
  useLastChanged,
  useMediaControls,
  useMounted,
  useMutationObserver,
  useNow,
  useResizeObserver,
  useScroll,
  useTimeoutFn,
  watchImmediate,
} from '@vueuse/core'
import { computed, nextTick, onBeforeMount, onBeforeUnmount, onMounted, ref, toValue, watch, watchEffect } from 'vue'
import { useComponentChain } from '@/utils/errorReportingUtils.js'
import { useRouter } from 'vue-router'
import consumer from '@/../channels/consumer.js'

export function useResponsive() {
  const bp = useBreakpoints({ mobile: 0, desktop: 768 }).active()

  return {
    mobile: computed(() => bp.value === 'mobile'),
    desktop: computed(() => bp.value === 'desktop'),
  }
}

export function useShared() {
  return {
    ...useI18n({
      inheritLocale: true,
      useScope: 'local',
    }),
    componentInfo: {
      chain: useComponentChain(),
    },
    ...useResponsive(),
  }
}

export function redirectUnless(cond, route) {
  onBeforeMount(() => {
    if (!cond) {
      const router = useRouter()
      router.push(route)
    }
  })
}

export function useBodyClass(classname) {
  onMounted(() => document.body.classList.add(classname))
  onBeforeUnmount(() => document.body.classList.remove(classname))
}

export function useBodyClassWhen(ref, classname) {
  watchImmediate(ref, on => document.body.classList.toggle(classname, on))

  onBeforeUnmount(() => document.body.classList.remove(classname))
}

export function setBodyCssVar(varname, value) {
  if (value === null) document.body.style.removeProperty(varname)
  else document.body.style.setProperty(varname, value)
}

export function setMobileBodyCssVar(varname, value) {
  const { mobile } = useResponsive()
  watchImmediate(mobile, on => setBodyCssVar(varname, on ? value : null))
}

export function useBodyCssVar(varname, value) {
  let initialValue

  onMounted(() => {
    initialValue = document.body.style.getPropertyValue(varname)
    setBodyCssVar(varname, value)
  })

  onBeforeUnmount(() => {
    setBodyCssVar(varname, initialValue)
  })
}

export function optionList(arr) {
  return arr.map(v => ({ value: v, label: String(v) }))
}

export function scrollBehavior(to, from, savedPosition) {
  if (from.path === to.path) {
    return { x: window.scrollX, y: window.scrollY }
  }

  return savedPosition ? savedPosition : { left: 0, top: 0 }
}

export function useSmoothScroll() {
  onMounted(() => (document.querySelector('html').style.scrollBehavior = 'smooth'))
  onBeforeUnmount(() => (document.querySelector('html').style.scrollBehavior = 'unset'))
}

export function useViewportInteractiveWidget(mode = 'resizes-visual') {
  const meta = document.querySelector('meta[name="viewport"]')
  const original = meta?.getAttribute('content') ?? ''
  const withoutWidget = original.replace(/,?\s*interactive-widget=[^,]*/g, '').trim()
  const updated = `${withoutWidget}, interactive-widget=${mode}`

  onMounted(() => meta?.setAttribute('content', updated))
  onBeforeUnmount(() => meta?.setAttribute('content', original))
}

export function useSmoothScrollToHash() {
  onMounted(() => {
    const hash = window.location.hash
    if (hash) {
      const element = document.querySelector(hash)
      if (element) {
        useTimeoutFn(() => element.scrollIntoView({ behavior: 'smooth' }), 0)
      }
    }
  })
}

export function useVideoControls(target, options) {
  const controls = useMediaControls(target, options)
  const played = ref(false)

  until(controls.playing)
    .toBeTruthy()
    .then(() => (played.value = true))

  const play = () => {
    toValue(target).play().catch(console.error)
  }

  const mutedPlay = () => {
    controls.muted.value = true
    toValue(target).play().catch(console.error)
  }

  const pause = () => {
    toValue(target).pause()
  }

  return {
    ...controls,
    played,
    play,
    mutedPlay,
    pause,
  }
}

export function useElementScroll(el) {
  const { x, y } = useScroll(el)
  const { width, height } = useElementSize(el)
  const xRemaining = ref(0)
  const yRemaining = ref(0)

  const setRemainingX = () =>
    (xRemaining.value = el.value ? el.value.scrollWidth - el.value.clientWidth - el.value.scrollLeft : 0)
  const setRemainingY = () =>
    (yRemaining.value = el.value ? el.value.scrollHeight - el.value.clientHeight - el.value.scrollTop : 0)

  watchImmediate([x, width], setRemainingX)
  watchImmediate([y, height], setRemainingY)

  useMutationObserver(
    el,
    () => {
      setRemainingX()
      setRemainingY()
    },
    { childList: true, subtree: true },
  )

  return { x, y, xRemaining, yRemaining }
}

export function useMutationObserverWithIgnores(target, callback, options = {}, ignoreOptions = {}) {
  const { ignoreAttribute = 'data-ignore-mutations' } = ignoreOptions

  const shouldIgnoreMutation = mutation => {
    const target = mutation.target

    // Check if target is an element node
    if (target.nodeType !== Node.ELEMENT_NODE) {
      return false
    }

    // Check if target or any ancestor has the ignore attribute
    if (ignoreAttribute && target.closest(`[${ignoreAttribute}]`)) {
      return true
    }

    return false
  }

  const wrappedCallback = mutations => {
    const filteredMutations = mutations.filter(mutation => !shouldIgnoreMutation(mutation))

    if (filteredMutations.length > 0) {
      callback(filteredMutations)
    }
  }

  return useMutationObserver(target, wrappedCallback, options)
}

export function useConsumerSubscription(channel, channelId, callbackFn, validateFn = () => true) {
  const called = refAutoReset(false, 0)
  const subscription = ref()

  const onReceived = (...args) => {
    if (validateFn(...args)) {
      called.value = true
      callbackFn(...args)
    }
  }

  onBeforeMount(async () => {
    await until(() => toValue(channel) && toValue(channelId)).toBeTruthy()

    subscription.value = consumer.subscriptions.create(
      {
        channel: toValue(channel),
        channel_id: toValue(channelId),
      },
      { received: onReceived },
    )
  })

  onBeforeUnmount(() => {
    subscription.value?.unsubscribe()
  })

  return { subscription, called }
}

export function useResponsiveOverride(targetRef, overrideVal, options = { desktop: true, mobile: true }) {
  const initial = targetRef.value
  const { desktop, mobile } = useResponsive()

  watchEffect(() => {
    const override = (desktop.value && options.desktop) || (mobile.value && options.mobile)
    targetRef.value = override ? overrideVal : initial
  })
}

export async function scrollToLastChildNode(_targetEl, _parentEl = null) {
  await nextTick()
  const targetEl = toValue(_targetEl)
  const parentEl = toValue(_parentEl) ?? targetEl
  if (!targetEl || !parentEl) return

  const lastNode = [...targetEl.children].at(-1)
  if (lastNode) {
    const maxScrollTop = parentEl.scrollHeight - parentEl.clientHeight
    const delta = lastNode.clientHeight - parentEl.clientHeight // when message longer than container
    parentEl.scrollTo({
      top: maxScrollTop - Math.max(0, delta),
      behavior: 'smooth',
    })
  }
}

export function useLastChangedRelative(source) {
  const now = useNow()
  const lastChangedAt = useLastChanged(source)
  return computed(() => now.value - lastChangedAt.value)
}

export function useMountedTime() {
  const now = useNow()
  const mounted = useMounted()
  const lastChangedAt = useLastChanged(mounted)
  return computed(() => (mounted.value ? now.value - lastChangedAt.value : 0))
}

export function useIsScrollable(target) {
  const isScrollable = ref(false)

  const check = () => {
    const el = toValue(target)
    if (el) {
      isScrollable.value = el.scrollHeight > el.clientHeight
    }
  }

  // Watch for content changes (children added/removed)
  useMutationObserver(target, check, {
    childList: true,
    subtree: true,
    characterData: true,
  })

  // Watch for resize (window resize, CSS changes)
  useResizeObserver(target, check)

  // Initial check
  onMounted(check)

  return isScrollable
}
